import type { ComponentType } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAdminAuth } from '../admin/AdminAuthContext'
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  useSidebarCollapsed,
} from '../hooks/useSidebarCollapsed'
import { AutoPageInfoBar, PageInfoProvider } from '../layout/PageInfoContext'
import { IconLogout } from './admin/adminIcons'
import { ProfferLogoMark } from './ProfferLogoMark'
import { SidebarToggleButton } from './SidebarToggleButton'
import { ThemeSwitcher } from './ThemeSwitcher'

type AdminNavItem = {
  to: string
  end?: boolean
  label: string
  description: string
  Icon: ComponentType<{ className?: string }>
}

function IconChart({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  )
}

function IconUsers({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766m-.001-.108a4.125 4.125 0 017.533-2.493M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  )
}

function IconBox({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5a1.125 1.125 0 00-1.125-1.125H3.375a1.125 1.125 0 00-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  )
}

function IconAnalytics({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  )
}

function IconCog({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function IconSparkles({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  )
}

function IconDocument({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

const primaryNav: AdminNavItem[] = [
  { to: '/admin', end: true, label: 'Επισκόπηση', description: 'KPIs', Icon: IconChart },
  { to: '/admin/users', label: 'Χρήστες', description: 'Plans & packages', Icon: IconUsers },
  { to: '/admin/packages', label: 'Πακέτα', description: 'Marketing copy', Icon: IconBox },
  { to: '/admin/analytics', label: 'Αναλυτικά', description: 'Funnels', Icon: IconAnalytics },
  { to: '/admin/operations', label: 'Λειτουργίες', description: 'Queues', Icon: IconCog },
]

const aiNav: AdminNavItem[] = [
  { to: '/admin/ai-usage', label: 'AI χρήση', description: 'OpenAI κόστος', Icon: IconSparkles },
  { to: '/admin/ai-pricing', label: 'AI τιμές', description: 'Υπολογισμός κόστους', Icon: IconCog },
  { to: '/admin/ai-roles', label: 'AI ρόλοι', description: 'Prompts', Icon: IconDocument },
]

/** Table list pages: scroll inside table only (no main scrollbar). */
const adminTableListPaths = ['/admin/users', '/admin/ai-roles', '/admin/ai-usage'] as const

const secondaryNav: AdminNavItem[] = [
  { to: '/admin/templates', label: 'Πρότυπα', description: 'Messages', Icon: IconDocument },
  { to: '/admin/settings', label: 'Ρυθμίσεις', description: 'Integrations', Icon: IconCog },
]

const ADMIN_GROUP_OPERATE = 'Λειτουργία'
const ADMIN_GROUP_AI = 'AI'
const ADMIN_GROUP_SYSTEM = 'Σύστημα'

function resolveAdminNav(pathname: string): {
  item: AdminNavItem
  groupTitle: string
} {
  const primary =
    primaryNav.find((item) =>
      item.end ? pathname === item.to : pathname.startsWith(item.to),
    ) ?? null
  if (primary) return { item: primary, groupTitle: ADMIN_GROUP_OPERATE }

  const ai =
    aiNav.find((item) => pathname.startsWith(item.to)) ?? null
  if (ai) return { item: ai, groupTitle: ADMIN_GROUP_AI }

  const secondary =
    secondaryNav.find((item) => pathname.startsWith(item.to)) ?? null
  if (secondary) return { item: secondary, groupTitle: ADMIN_GROUP_SYSTEM }

  return { item: primaryNav[0], groupTitle: ADMIN_GROUP_OPERATE }
}

function AdminHeaderPageContext({
  item,
  groupTitle,
}: {
  item: AdminNavItem
  groupTitle: string
}) {
  const { Icon, label } = item

  return (
    <div
      className="flex min-w-0 max-w-[min(100%,18rem)] items-center gap-2 sm:max-w-xs"
      aria-label={`${groupTitle}: ${label}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 leading-tight">
        <p className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-400">
          {groupTitle}
        </p>
        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-white">
          {label}
        </p>
      </div>
    </div>
  )
}

const navClassExpanded = ({ isActive }: { isActive: boolean }) =>
  `group flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5 text-sm font-semibold transition ${
    isActive
      ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/20'
      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white'
  }`

const navClassCollapsed = ({ isActive }: { isActive: boolean }) =>
  `group flex items-center justify-center rounded-2xl p-2.5 transition ${
    isActive
      ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/20'
      : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white'
  }`

function NavGroup({
  title,
  items,
  collapsed,
}: {
  title: string
  items: AdminNavItem[]
  collapsed: boolean
}) {
  return (
    <div>
      {!collapsed ? (
        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500">
          {title}
        </p>
      ) : null}
      <nav className="space-y-1" aria-label={title}>
        {items.map((item) => {
          const { Icon } = item
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              className={collapsed ? navClassCollapsed : navClassExpanded}
            >
              {collapsed ? (
                <Icon className="h-5 w-5 shrink-0" />
              ) : (
                <>
                  <span className="flex items-center gap-2">
                    <Icon className="h-5 w-5 shrink-0 opacity-80" />
                    <span>{item.label}</span>
                  </span>
                  <span className="hidden text-[11px] font-medium opacity-70 xl:inline">
                    {item.description}
                  </span>
                </>
              )}
            </NavLink>
          )
        })}
      </nav>
    </div>
  )
}

export function AdminShell() {
  const { logout } = useAdminAuth()
  const { pathname } = useLocation()
  const { collapsed, toggle } = useSidebarCollapsed('admin')
  const { item: activeNav, groupTitle: activeGroupTitle } =
    resolveAdminNav(pathname)
  const isTableListPage = adminTableListPaths.some((p) => pathname === p)

  return (
    <div className="relative min-h-dvh bg-zinc-100 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-50">
      <div
        className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(124,58,237,0.16),transparent_65%)] dark:bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(124,58,237,0.10),transparent_65%)]"
        aria-hidden
      />
      <div className="relative flex min-h-dvh">
        <aside
          style={{
            width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
          }}
          className="sticky top-0 hidden h-dvh shrink-0 overflow-y-auto border-r border-zinc-200/80 bg-white/85 shadow-app-soft backdrop-blur-xl transition-[width] duration-200 ease-out dark:border-zinc-800 dark:bg-zinc-950/75 lg:block"
        >
          <div
            className={`flex min-h-dvh flex-col py-5 ${
              collapsed ? 'px-2' : 'px-4'
            }`}
          >
            <div
              className={`mb-8 flex gap-2 ${
                collapsed
                  ? 'flex-col items-center'
                  : 'items-start justify-between'
              }`}
            >
              <Link
                to="/admin"
                title="ProfferAI Admin"
                className={`flex items-center rounded-app ${
                  collapsed ? 'justify-center' : 'min-w-0 flex-1 gap-3 px-2'
                }`}
              >
                <ProfferLogoMark className="h-11 w-11" rounded="app" />
                {!collapsed ? (
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold tracking-tight text-zinc-950 dark:text-white">
                      ProfferAI Admin
                    </span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
                      Console
                    </span>
                  </span>
                ) : null}
              </Link>
              <SidebarToggleButton
                expanded={!collapsed}
                onToggle={toggle}
                className="shrink-0"
              />
            </div>

            <div className={collapsed ? 'space-y-3' : 'space-y-7'}>
              <NavGroup title="Operate" items={primaryNav} collapsed={collapsed} />
              <NavGroup title="AI" items={aiNav} collapsed={collapsed} />
              <NavGroup title="System" items={secondaryNav} collapsed={collapsed} />
            </div>

            <div
              className={`mt-auto rounded-app-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/70 ${
                collapsed ? 'flex flex-col items-center gap-2 p-2' : 'p-3'
              }`}
            >
              {!collapsed ? (
                <p className="text-xs font-semibold text-zinc-900 dark:text-white">
                  Admin session
                </p>
              ) : null}
              <div
                className={`flex gap-2 ${collapsed ? 'flex-col' : 'mt-3'}`}
              >
                <Link
                  to="/"
                  title="App"
                  className={`inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800 ${
                    collapsed ? 'h-9 w-9' : 'flex-1 px-3 py-2'
                  }`}
                >
                  {collapsed ? 'A' : 'App'}
                </Link>
                <button
                  type="button"
                  title="Logout"
                  onClick={() => logout()}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl bg-zinc-900 text-xs font-semibold text-white transition hover:bg-zinc-700 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-200 ${
                    collapsed ? 'h-9 w-9' : 'flex-1 px-3 py-2'
                  }`}
                >
                  <IconLogout className={collapsed ? 'h-4 w-4' : 'h-4 w-4 shrink-0'} />
                  {!collapsed ? 'Logout' : null}
                </button>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex h-dvh min-w-0 flex-1 flex-col overflow-hidden">
          <header className="relative z-30 shrink-0 border-b border-zinc-200/70 bg-white/95 shadow-[0_1px_0_rgba(0,0,0,0.03)] backdrop-blur-2xl dark:border-zinc-800/70 dark:bg-zinc-950/95 sm:px-6 lg:px-8">
            <div className="mx-auto w-full max-w-app-wide px-4 sm:px-6">
            <div className="flex h-[3.75rem] items-center justify-between gap-3 sm:h-16">
              <AdminHeaderPageContext
                item={activeNav}
                groupTitle={activeGroupTitle}
              />
              <div className="flex shrink-0 items-center gap-2">
                <ThemeSwitcher />
                <button
                  type="button"
                  onClick={() => logout()}
                  className="hidden items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800 sm:inline-flex"
                >
                  <IconLogout />
                  Logout
                </button>
              </div>
            </div>
            <nav
              className="flex gap-2 overflow-x-auto border-t border-zinc-100 py-2.5 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] lg:hidden dark:border-zinc-800/80 [&::-webkit-scrollbar]:hidden"
              aria-label="Admin mobile navigation"
            >
              {[...primaryNav, ...aiNav, ...secondaryNav].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'bg-violet-600 text-white shadow-md shadow-violet-600/20'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            </div>
          </header>

          <main
            className={`flex min-h-0 min-w-0 flex-1 flex-col px-4 py-6 sm:px-6 lg:px-8 lg:py-8 ${
              isTableListPage ? 'overflow-hidden' : 'overflow-y-auto'
            }`}
          >
            <PageInfoProvider>
              <div className="mx-auto flex min-h-0 w-full max-w-app-wide flex-1 flex-col">
                <AutoPageInfoBar className="mb-4 shrink-0" />
                <div className="flex min-h-0 flex-1 flex-col">
                  <Outlet />
                </div>
              </div>
            </PageInfoProvider>
          </main>
          <footer className="shrink-0 border-t border-zinc-200/80 px-4 py-4 text-xs text-zinc-500 dark:border-zinc-800 dark:text-zinc-500 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="font-semibold text-violet-700 hover:underline dark:text-violet-300"
            >
              Open user app
            </Link>
            <span className="mx-2">/</span>
            ProfferAI Administrative console
          </footer>
        </div>
      </div>
    </div>
  )
}
